import { useEffect, useRef, useState } from "react";
import { fmtCompact, fmtMonth, fmtSigned, fmtUnits } from "../format";

function useWidth<T extends HTMLElement>(): [React.RefObject<T>, number] {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(600);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setWidth(Math.max(240, entry.contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

/** "Nice" ticks covering [min, max], always including zero when the data straddles it. */
function niceTicks(min: number, max: number, count = 5): number[] {
  if (min === max) { max = min + 1; min = Math.min(0, min); }
  const span = max - min;
  const step0 = span / count;
  const mag = Math.pow(10, Math.floor(Math.log10(step0)));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => span / s <= count) ?? 10 * mag;
  const start = Math.floor(min / step) * step;
  const ticks = [];
  for (let v = start; v <= max + step * 0.5; v += step) ticks.push(Math.round(v / step) * step);
  return ticks;
}

// ── line chart: one axis, 2px lines, crosshair tooltip ─────────────────────
export interface LineSeries { name: string; color: string; dashed?: boolean; values: (number | null)[] }

/** A shaded range (e.g. pessimistic .. optimistic) drawn behind the lines. */
export interface LineBand { name: string; color: string; low: (number | null)[]; high: (number | null)[] }

export function LineChart({ labels, series, height = 240, format = fmtUnits, highlight, marker, markerLabel = "Run date", bands = [] }: {
  labels: string[]; series: LineSeries[]; height?: number; format?: (v: number) => string;
  /** Periods to shade (e.g. the months an intent changed), same length as labels. */
  highlight?: boolean[];
  /** Index of the period where history ends and the forecast begins: a thin vertical rule. */
  marker?: number;
  markerLabel?: string;
  bands?: LineBand[];
}) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const M = { top: 12, right: 56, bottom: 26, left: 52 };
  const all = [...series.flatMap((s) => s.values), ...bands.flatMap((b) => [...b.low, ...b.high])]
    .filter((v): v is number => v != null);
  if (!labels.length || !all.length) return <div className="empty">No data in this range.</div>;
  const lo = Math.min(...all);
  const hi = Math.max(...all);
  const pad = (hi - lo) * 0.08 || Math.abs(hi) * 0.1 || 1;
  const ticks = niceTicks(lo >= 0 ? Math.max(0, lo - pad) : lo - pad, hi + pad);
  const y0 = ticks[0];
  const y1 = ticks[ticks.length - 1];
  const iw = width - M.left - M.right;
  const ih = height - M.top - M.bottom;
  const x = (i: number) => M.left + (labels.length === 1 ? iw / 2 : (i / (labels.length - 1)) * iw);
  const y = (v: number) => M.top + ih - ((v - y0) / (y1 - y0)) * ih;
  const step = Math.max(1, Math.ceil(labels.length / Math.max(2, Math.floor(iw / 70))));

  const onMove = (e: React.PointerEvent<SVGRectElement>) => {
    const rect = (e.currentTarget.ownerSVGElement as SVGSVGElement).getBoundingClientRect();
    const px = e.clientX - rect.left;
    const i = Math.round(((px - M.left) / iw) * (labels.length - 1));
    setHover(Math.min(labels.length - 1, Math.max(0, i)));
  };

  return (
    <div className="chart" ref={ref}>
      <div className="legend" style={{ marginBottom: 6 }}>
        {series.map((s) => (
          <span className="key" key={s.name}>
            <span className={`swatch${s.dashed ? " dashed" : ""}`} style={{ borderColor: s.color }} />{s.name}
          </span>
        ))}
      </div>
      <svg height={height} role="img" aria-label={`${series.map((s) => s.name).join(" and ")} by month`}>
        {highlight?.some(Boolean) && (() => {
          // One band per period, centred on its point and half a step wide either side.
          const half = labels.length > 1 ? iw / (labels.length - 1) / 2 : iw / 2;
          return (
            <g className="highlight">
              {labels.map((l, i) => highlight[i] && (
                <rect key={l} x={Math.max(M.left, x(i) - half)} y={M.top}
                      width={Math.min(x(i) + half, M.left + iw) - Math.max(M.left, x(i) - half)} height={ih} />
              ))}
            </g>
          );
        })()}
        <g className="grid">
          {ticks.map((t) => <line key={t} x1={M.left} x2={width - M.right} y1={y(t)} y2={y(t)} />)}
        </g>
        {bands.map((b) => {
          // Polygon over the periods where both edges exist: along the top, back along the bottom.
          const idx = labels.map((_, i) => i).filter((i) => b.low[i] != null && b.high[i] != null);
          if (idx.length < 2) return null;
          const top = idx.map((i) => `${x(i)},${y(b.high[i]!)}`);
          const bottom = [...idx].reverse().map((i) => `${x(i)},${y(b.low[i]!)}`);
          return <polygon key={b.name} points={[...top, ...bottom].join(" ")} fill={b.color} opacity={0.14} />;
        })}
        {marker != null && marker >= 0 && marker < labels.length && (
          <g className="marker">
            <line x1={x(marker)} x2={x(marker)} y1={M.top} y2={M.top + ih} stroke="var(--axis)" strokeDasharray="2 3" />
            <text x={x(marker) + 4} y={M.top + 10} className="marker-label">{markerLabel}</text>
          </g>
        )}
        {ticks.map((t) => (
          <text key={t} x={M.left - 8} y={y(t)} dy="0.32em" textAnchor="end">{fmtCompact(t)}</text>
        ))}
        {labels.map((l, i) => (i % step === 0 || i === labels.length - 1) && (
          <text key={l} x={x(i)} y={height - 6} textAnchor="middle">{fmtMonth(l)}</text>
        ))}
        {/* drawn last-first so the first series (the subject) sits on top where lines coincide */}
        {[...series].reverse().map((s) => {
          const pts = s.values.map((v, i) => (v == null ? null : `${x(i)},${y(v)}`)).filter(Boolean);
          return (
            <polyline key={s.name} points={pts.join(" ")} fill="none" stroke={s.color} strokeWidth={2}
                      strokeDasharray={s.dashed ? "5 4" : undefined} strokeLinejoin="round" strokeLinecap="round" />
          );
        })}
        {/* direct labels at the line ends (≤ 4 series), nudged apart so they never overlap */}
        {series.length <= 4 && endLabels(series, x, y).map((l) => (
          <text key={l.name} className="label" x={l.x + 6} y={l.y} dy="0.32em">{l.name}</text>
        ))}
        {hover != null && (
          <g>
            <line x1={x(hover)} x2={x(hover)} y1={M.top} y2={M.top + ih} stroke="var(--axis)" />
            {series.map((s) => s.values[hover] != null && (
              <circle key={s.name} cx={x(hover)} cy={y(s.values[hover]!)} r={4.5} fill={s.color}
                      stroke="var(--surface)" strokeWidth={2} />
            ))}
          </g>
        )}
        <rect x={M.left} y={M.top} width={iw} height={ih} fill="transparent"
              onPointerMove={onMove} onPointerLeave={() => setHover(null)} />
      </svg>
      {hover != null && (
        <div className="tooltip" style={{ left: Math.min(x(hover) + 12, width - 170), top: 28 }}>
          <div className="t-head">{fmtMonth(labels[hover])}</div>
          {bands.map((b) => b.low[hover] != null && (
            <div className="t-row" key={b.name}>
              <span className="swatch" style={{ borderColor: b.color, opacity: 0.5 }} />
              <strong>{format(b.low[hover]!)} – {format(b.high[hover]!)}</strong>
              <span className="muted">{b.name}</span>
            </div>
          ))}
          {series.map((s) => (
            <div className="t-row" key={s.name}>
              <span className={`swatch`} style={{ borderColor: s.color, borderTopStyle: s.dashed ? "dashed" : "solid" }} />
              <strong>{s.values[hover] == null ? "—" : format(s.values[hover]!)}</strong>
              <span className="muted">{s.name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function endLabels(series: LineSeries[], x: (i: number) => number, y: (v: number) => number) {
  const MIN_GAP = 14;
  const labels = series.flatMap((s) => {
    const lastIdx = s.values.map((v) => v != null).lastIndexOf(true);
    return lastIdx < 0 ? [] : [{ name: s.name, x: x(lastIdx), y: y(s.values[lastIdx]!) }];
  }).sort((a, b) => a.y - b.y);
  for (let i = 1; i < labels.length; i++) {
    if (labels[i].y - labels[i - 1].y < MIN_GAP) {
      const mid = (labels[i].y + labels[i - 1].y) / 2;
      labels[i - 1].y = Math.min(labels[i - 1].y, mid - MIN_GAP / 2);
      labels[i].y = labels[i - 1].y + MIN_GAP;
    }
  }
  return labels;
}

// ── diverging horizontal bars: blue up, red down, zero in the middle ─────────
export function DivergingBars({ rows, format = fmtSigned }: {
  rows: { label: string; value: number; note?: string }[]; format?: (v: number) => string;
}) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  if (!rows.length) return <div className="empty">No changes against the baseline.</div>;
  const BAR = 18;
  const GAP = 10;
  const labelW = Math.min(180, width * 0.32);
  const valueW = 70;
  const plotW = width - labelW - valueW * 2;
  const max = Math.max(...rows.map((r) => Math.abs(r.value)), 1);
  const cx = labelW + valueW + plotW / 2;
  const scale = (plotW / 2) / max;
  const height = rows.length * (BAR + GAP);
  return (
    <div className="chart" ref={ref}>
      <svg height={height} role="img" aria-label="Change against baseline">
        <line className="axis-line" x1={cx} x2={cx} y1={0} y2={height} />
        {rows.map((r, i) => {
          const top = i * (BAR + GAP);
          const w = Math.max(r.value === 0 ? 0 : 2, Math.abs(r.value) * scale);
          const pos = r.value >= 0;
          const bx = pos ? cx : cx - w;
          return (
            <g key={r.label} onPointerEnter={() => setHover(i)} onPointerLeave={() => setHover(null)}>
              <rect x={0} y={top - GAP / 2} width={width} height={BAR + GAP} fill="transparent" />
              <text className="label" x={0} y={top + BAR / 2} dy="0.32em">{r.label}</text>
              <rect x={bx} y={top} width={w} height={BAR} rx={4}
                    fill={pos ? "var(--series-target)" : "var(--series-neg)"} opacity={hover == null || hover === i ? 1 : 0.55} />
              <text className="value" x={pos ? bx + w + 6 : bx - 6} y={top + BAR / 2} dy="0.32em"
                    textAnchor={pos ? "start" : "end"}>{format(r.value)}</text>
            </g>
          );
        })}
      </svg>
      {hover != null && rows[hover].note && (
        <div className="tooltip" style={{ left: cx - 75, top: hover * (BAR + GAP) + BAR + 4 }}>
          <div className="t-head">{rows[hover].label}</div>
          <div>{rows[hover].note}</div>
        </div>
      )}
    </div>
  );
}

// ── signed columns (e.g. quantity written per month) ────────────────────────
export function ColumnChart({ rows, height = 200, name }: { rows: { label: string; value: number }[]; height?: number; name: string }) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  if (!rows.length) return <div className="empty">Nothing written.</div>;
  const M = { top: 14, right: 12, bottom: 26, left: 52 };
  const vals = rows.map((r) => r.value);
  const ticks = niceTicks(Math.min(0, ...vals), Math.max(0, ...vals));
  const y0 = ticks[0];
  const y1 = ticks[ticks.length - 1];
  const iw = width - M.left - M.right;
  const ih = height - M.top - M.bottom;
  const y = (v: number) => M.top + ih - ((v - y0) / (y1 - y0)) * ih;
  const band = iw / rows.length;
  const bw = Math.min(36, band - 4);
  return (
    <div className="chart" ref={ref}>
      <svg height={height} role="img" aria-label={`${name} by month`}>
        <g className="grid">{ticks.map((t) => <line key={t} x1={M.left} x2={width - M.right} y1={y(t)} y2={y(t)} />)}</g>
        {ticks.map((t) => <text key={t} x={M.left - 8} y={y(t)} dy="0.32em" textAnchor="end">{fmtCompact(t)}</text>)}
        <line className="axis-line" x1={M.left} x2={width - M.right} y1={y(0)} y2={y(0)} />
        {rows.map((r, i) => {
          const cx = M.left + band * i + band / 2;
          const top = Math.min(y(r.value), y(0));
          const h = Math.max(1, Math.abs(y(r.value) - y(0)));
          return (
            <g key={r.label} onPointerEnter={() => setHover(i)} onPointerLeave={() => setHover(null)}>
              <rect x={cx - band / 2} y={M.top} width={band} height={ih} fill="transparent" />
              <rect x={cx - bw / 2} y={top} width={bw} height={h} rx={4}
                    fill={r.value >= 0 ? "var(--series-target)" : "var(--series-neg)"} />
              {(rows.length <= 8 || i % Math.ceil(rows.length / 8) === 0) && (
                <text x={cx} y={height - 6} textAnchor="middle">{fmtMonth(r.label)}</text>
              )}
            </g>
          );
        })}
      </svg>
      {hover != null && (
        <div className="tooltip" style={{ left: Math.min(M.left + band * hover + band / 2 + 8, width - 160), top: 8 }}>
          <div className="t-head">{fmtMonth(rows[hover].label)}</div>
          <div className="t-row"><strong>{fmtSigned(rows[hover].value)}</strong><span className="muted">{name}</span></div>
        </div>
      )}
    </div>
  );
}
